import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/portfolio/Navbar";
import { 
  ArrowLeft, 
  ArrowUpRight, 
  Building2, 
  Layers, 
  Globe, 
  Calendar, 
  User, 
  CheckCircle2, 
  ArrowRight,
  TrendingUp,
  Share2,
  AlertTriangle,
  Sparkles,
  ShieldCheck,
  GitBranch,
  Clock,
  Check,
  Zap,
  BarChart3
} from "lucide-react";

export default function SeismicCaseStudyPage() {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const originalTitle = document.title;
    const bestTitle = "Seismic SEO Case Study: 8,000+ Page Migration & AI Search Lift | Kanna Narendra Kumar";
    const bestDesc = "How Kanna Narendra Kumar managed Seismic's 8,000+ page WordPress to Sitecore migration—reversing an initial 20% traffic drop in 90 days and boosting AI search visibility by 120%.";

    document.title = bestTitle;

    // Helper for meta tags
    const setMeta = (selector, attr, value) => {
      let el = document.querySelector(selector);
      if (el) {
        el.setAttribute(attr, value);
      } else {
        const meta = document.createElement("meta");
        const isProp = selector.includes("property=");
        if (isProp) {
          const match = selector.match(/property="([^"]+)"/);
          if (match) meta.setAttribute("property", match[1]);
        } else {
          const match = selector.match(/name="([^"]+)"/);
          if (match) meta.setAttribute("name", match[1]);
        }
        meta.setAttribute(attr, value);
        document.head.appendChild(meta);
      }
    };

    // Set Meta Description
    let metaDesc = document.querySelector('meta[name="description"]');
    const originalDesc = metaDesc ? metaDesc.getAttribute("content") : "";
    setMeta('meta[name="description"]', "content", bestDesc);

    // Set Social / OG Meta Tags
    setMeta('meta[property="og:title"]', "content", bestTitle);
    setMeta('meta[property="og:description"]', "content", bestDesc);
    setMeta('meta[property="og:url"]', "content", "https://narendradigital.in/casestudy/seismic/");
    setMeta('meta[property="og:type"]', "content", "article");
    setMeta('meta[name="twitter:title"]', "content", bestTitle);
    setMeta('meta[name="twitter:description"]', "content", bestDesc);

    // Set Canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    let createdCanonical = false;
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
      createdCanonical = true;
    }
    const originalCanonical = canonical ? canonical.getAttribute("href") : null;
    if (canonical) {
      canonical.setAttribute("href", "https://narendradigital.in/casestudy/seismic/");
    }

    // Add JSON-LD Schema
    const scriptId = "seismic-case-study-schema";
    let scriptTag = document.getElementById(scriptId);
    if (!scriptTag) {
      scriptTag = document.createElement("script");
      scriptTag.id = scriptId;
      scriptTag.type = "application/ld+json";
      scriptTag.text = JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": "Seismic SEO Case Study: 8,000+ Page Migration & AI Search Lift",
        "description": bestDesc,
        "author": {
          "@type": "Person",
          "name": "Kanna Narendra Kumar",
          "jobTitle": "Senior SEO Specialist & AI Search Strategist",
          "url": "https://narendradigital.in/"
        },
        "publisher": {
          "@type": "Person",
          "name": "Kanna Narendra Kumar"
        },
        "mainEntityOfPage": "https://narendradigital.in/casestudy/seismic/"
      });
      document.head.appendChild(scriptTag);
    }

    window.scrollTo(0, 0);

    return () => {
      document.title = originalTitle;
      if (metaDesc && originalDesc) metaDesc.setAttribute("content", originalDesc);
      if (canonical) {
        if (createdCanonical) {
          canonical.remove();
        } else if (originalCanonical) {
          canonical.setAttribute("href", originalCanonical);
        }
      }
      if (scriptTag) scriptTag.remove();
    };
  }, []);

  const handleShare = () => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <div className="bg-[#080c16] text-[#e2e8f0] min-h-screen font-sans selection:bg-[#3b82f6] selection:text-white antialiased relative overflow-x-hidden">
      
      {/* Main Global Navigation Bar */}
      <Navbar />

      {/* Ambient soft background lighting */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[800px] h-[450px] bg-gradient-to-b from-blue-600/15 via-cyan-500/10 to-transparent blur-[120px] -z-10" />
      <div className="pointer-events-none absolute top-[900px] -left-48 w-[500px] h-[500px] bg-indigo-600/10 blur-[140px] -z-10" />
      <div className="pointer-events-none absolute top-[2200px] -right-48 w-[500px] h-[500px] bg-emerald-600/10 blur-[140px] -z-10" />

      {/* Main Container */}
      <main className="max-w-4xl mx-auto px-5 sm:px-8 pt-28 sm:pt-32 pb-16">

        {/* Back Link & Share Utility Bar */}
        <div className="flex items-center justify-between gap-4 mb-8 pb-3 border-b border-white/[0.08]">
          <Link
            to="/"
            className="group inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-slate-400 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-1 text-cyan-400" />
            <span>Back to Portfolio</span>
          </Link>

          <div className="flex items-center gap-3 text-xs font-mono">
            <button
              onClick={handleShare}
              className="px-2.5 py-1 rounded-md border border-white/10 bg-white/[0.03] hover:bg-white/[0.08] text-slate-300 hover:text-white transition-colors flex items-center gap-1.5"
              title="Copy Case Study URL"
            >
              {copied ? (
                <>
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                  <span className="text-emerald-400">Copied Link</span>
                </>
              ) : (
                <>
                  <Share2 className="w-3.5 h-3.5" />
                  <span>Share</span>
                </>
              )}
            </button>
            <a
              href="mailto:narendrakumarkanna@gmail.com"
              className="text-cyan-400 hover:text-white transition-colors flex items-center gap-1 font-semibold"
            >
              <span>Contact</span>
              <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

        {/* HERO SECTION */}
        <section className="mb-14">
          
          {/* Tag Badges */}
          <div className="flex flex-wrap items-center gap-2 text-xs font-mono text-slate-400 mb-6">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-blue-500/10 border border-blue-500/20 text-blue-300 font-semibold shadow-inner">
              <Building2 className="w-3.5 h-3.5 text-blue-400" />
              <span>Seismic Software</span>
            </div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 font-semibold">
              <GitBranch className="w-3.5 h-3.5 text-cyan-400" />
              <span>WordPress → Sitecore</span>
            </div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 font-semibold">
              <Layers className="w-3.5 h-3.5 text-emerald-400" />
              <span>8,000+ Pages</span>
            </div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-purple-300 font-semibold">
              <Sparkles className="w-3.5 h-3.5 text-purple-400" />
              <span>AI Search Ready</span>
            </div>
          </div>

          {/* Main Title */}
          <h1 className="font-heading font-extrabold text-3xl sm:text-5xl lg:text-[44px] text-white tracking-tight leading-[1.2] mb-6">
            Turning a Website Migration Into a{" "}
            <span className="bg-gradient-to-r from-blue-400 via-cyan-300 to-teal-300 bg-clip-text text-transparent">
              Search &amp; AI Visibility
            </span>{" "}
            Opportunity
          </h1>

          {/* Simple, Clear Subtitle */}
          <p className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-3xl mb-8">
            How I took full ownership of an 8,000-page enterprise website migration, stopped an initial 20% traffic drop, fully recovered rankings in 90 days, and boosted AI search visibility by 120%.
          </p>


          {/* Author Card */}
          <div className="p-4 sm:p-5 rounded-2xl bg-white/[0.03] border border-white/10 backdrop-blur-sm shadow-xl flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3.5">
              <div className="relative">
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-600 via-indigo-600 to-cyan-400 text-white flex items-center justify-center font-bold text-sm shadow-md shadow-blue-500/20">
                  KN
                </div>
                <span className="absolute -bottom-1 -right-1 w-3.5 h-3.5 bg-emerald-400 border-2 border-[#080c16] rounded-full" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-white font-semibold text-sm">Kanna Narendra Kumar</p>
                  <span className="text-[10px] font-mono uppercase px-1.5 py-0.5 rounded bg-cyan-400/10 text-cyan-300 border border-cyan-400/20">SEO Lead</span>
                </div>
                <p className="text-xs text-slate-400">Senior SEO Specialist &amp; AI Search Strategist</p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-xs font-mono text-slate-400 pt-3 sm:pt-0 border-t sm:border-t-0 border-white/10">
              <div className="flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-slate-500" />
                <span>5 min read</span>
              </div>
              <div className="flex items-center gap-1.5">
                <Globe className="w-3.5 h-3.5 text-slate-500" />
                <span>B2B Enterprise SaaS</span>
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-emerald-400 font-medium">Verified Results</span>
              </div>
            </div>
          </div>
        </section>

        {/* 3 KEY METRICS CARDS (Removed the 8,000+ card as requested) */}
        <section className="mb-14">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            
            {/* Metric 1 */}
            <div className="p-5 rounded-2xl bg-gradient-to-b from-white/[0.05] to-white/[0.01] border border-white/10 hover:border-cyan-500/40 transition-all duration-300">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-mono uppercase tracking-wider text-slate-400 font-medium">Recovery Time</span>
                <div className="w-7 h-7 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 flex items-center justify-center">
                  <Clock className="w-3.5 h-3.5" />
                </div>
              </div>
              <div className="font-heading font-extrabold text-3xl text-white tracking-tight">
                90 Days
              </div>
              <p className="text-xs text-slate-400 mt-1.5 leading-snug">
                Fixed the 20% traffic drop and brought traffic back to normal within 3 months.
              </p>
              <div className="mt-3 pt-2.5 border-t border-white/5 flex items-center gap-1 text-[11px] font-mono text-cyan-400">
                <TrendingUp className="w-3 h-3" />
                <span>Back to Normal Traffic</span>
              </div>
            </div>

            {/* Metric 2 */}
            <div className="p-5 rounded-2xl bg-gradient-to-b from-white/[0.05] to-white/[0.01] border border-white/10 hover:border-emerald-500/40 transition-all duration-300">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-mono uppercase tracking-wider text-slate-400 font-medium">AI Search Lift</span>
                <div className="w-7 h-7 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 flex items-center justify-center">
                  <Sparkles className="w-3.5 h-3.5" />
                </div>
              </div>
              <div className="font-heading font-extrabold text-3xl text-emerald-400 tracking-tight">
                +120%
              </div>
              <p className="text-xs text-slate-400 mt-1.5 leading-snug">
                Growth in visibility across Google AI Overviews and Bing Copilot answers.
              </p>
              <div className="mt-3 pt-2.5 border-t border-white/5 flex items-center gap-1 text-[11px] font-mono text-emerald-400">
                <Zap className="w-3 h-3" />
                <span>Featured in AI Answers</span>
              </div>
            </div>

            {/* Metric 3 */}
            <div className="p-5 rounded-2xl bg-gradient-to-b from-white/[0.05] to-white/[0.01] border border-white/10 hover:border-indigo-500/40 transition-all duration-300">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] font-mono uppercase tracking-wider text-slate-400 font-medium">Business Leads</span>
                <div className="w-7 h-7 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 flex items-center justify-center">
                  <BarChart3 className="w-3.5 h-3.5" />
                </div>
              </div>
              <div className="font-heading font-extrabold text-3xl text-indigo-400 tracking-tight">
                ~30%
              </div>
              <p className="text-xs text-slate-400 mt-1.5 leading-snug">
                Protected the main demo and product pages that bring in software sales leads.
              </p>
              <div className="mt-3 pt-2.5 border-t border-white/5 flex items-center gap-1 text-[11px] font-mono text-indigo-400">
                <ShieldCheck className="w-3 h-3" />
                <span>Sales Leads Protected</span>
              </div>
            </div>

          </div>
        </section>

        {/* SECTION 1: OVERVIEW & TURNING POINT */}
        <section id="overview" className="scroll-mt-24 mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-cyan-400" />
            <h2 className="text-xs font-mono uppercase tracking-widest text-cyan-400 font-bold">
              01 / The Story &amp; Overview
            </h2>
          </div>

          <div className="space-y-4 text-base leading-relaxed text-slate-300 mb-8">
            <p>
              Seismic was in the middle of moving its entire website from <strong className="text-white font-semibold">WordPress to Sitecore</strong>. The website was massive, with around <strong className="text-white font-semibold">8,000 pages</strong>—including product pages, customer stories, blogs, guides, and international versions.
            </p>
          </div>

          {/* The Crisis / Turning Point Callout Card */}
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-amber-500/10 via-orange-500/5 to-transparent border border-amber-500/30 p-6 sm:p-7 shadow-lg mb-8">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/30 text-amber-300 flex items-center justify-center flex-shrink-0 mt-0.5">
                <AlertTriangle className="w-5 h-5 text-amber-400" />
              </div>
              <div className="space-y-2">
                <span className="text-xs font-mono uppercase tracking-wider text-amber-400 font-bold px-2 py-0.5 rounded bg-amber-400/10 border border-amber-400/20 inline-block">
                  The Turning Point
                </span>
                <h3 className="font-heading font-bold text-lg sm:text-xl text-white">
                  The Previous SEO Lead Left During the Migration
                </h3>
                <p className="text-sm sm:text-base text-amber-100/90 leading-relaxed">
                  Right in the middle of the project, the SEO owner left the company. The website had already lost around 20% of its organic traffic because of broken redirect links and missing tags. I immediately stepped in, took 100% control of the SEO work, and partnered directly with the web developers to fix the issues fast.
                </p>
              </div>
            </div>
          </div>

          <div className="p-5 rounded-xl bg-white/[0.02] border border-white/10 text-sm leading-relaxed text-slate-300">
            <p>
              My goal was straightforward: <strong className="text-white">stop the traffic loss immediately</strong>, bring our Google rankings back to normal, and set up the content so it ranks well in <strong className="text-cyan-300">new AI search engines</strong> like Google AI Overviews and Copilot.
            </p>
          </div>
        </section>

        {/* SECTION 2: THE CHALLENGE */}
        <section id="challenge" className="scroll-mt-24 mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-cyan-400" />
            <h2 className="text-xs font-mono uppercase tracking-widest text-cyan-400 font-bold">
              02 / The Challenge
            </h2>
          </div>

          <h3 className="font-heading font-bold text-2xl sm:text-3xl text-white tracking-tight mb-4">
            Why Moving 8,000 Pages Was So Risky
          </h3>
          <p className="text-base text-slate-300 leading-relaxed mb-6">
            Moving a large website to a new system is like moving thousands of houses to new streets. If the signs (redirects) are wrong, visitors and search engines get lost, pages disappear from Google, and business revenue drops.
          </p>

          {/* 4 Challenge Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            
            <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="w-8 h-8 rounded-lg bg-blue-500/10 text-blue-400 flex items-center justify-center font-mono text-xs font-bold mb-3">
                01
              </div>
              <h4 className="font-heading font-bold text-base text-white mb-2">
                Sorting Out 8,000 Pages
              </h4>
              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                We had to inspect thousands of old pages to decide: which ones bring real traffic, which ones should be combined, which should be redirected, and which old useless pages should be removed.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="w-8 h-8 rounded-lg bg-cyan-500/10 text-cyan-400 flex items-center justify-center font-mono text-xs font-bold mb-3">
                02
              </div>
              <h4 className="font-heading font-bold text-base text-white mb-2">
                Broken Redirect Links
              </h4>
              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                The development team had set up broad redirect rules that sent some visitors to the wrong pages or created redirect loops. This caused Google to drop page rankings.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="w-8 h-8 rounded-lg bg-indigo-500/10 text-indigo-400 flex items-center justify-center font-mono text-xs font-bold mb-3">
                03
              </div>
              <h4 className="font-heading font-bold text-base text-white mb-2">
                Old Internal Links Everywhere
              </h4>
              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                Thousands of blog posts and articles were still pointing to old WordPress URLs instead of the new Sitecore URLs, which slowed down the website and confused Google crawlers.
              </p>
            </div>

            <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/10 text-emerald-400 flex items-center justify-center font-mono text-xs font-bold mb-3">
                04
              </div>
              <h4 className="font-heading font-bold text-base text-white mb-2">
                The New World of AI Search
              </h4>
              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                Search was changing fast with Google AI Overviews and Copilot. We needed to write and format our pages so AI systems could easily read and quote Seismic as the best answer.
              </p>
            </div>

          </div>

          <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10">
            <p className="text-xs font-mono uppercase tracking-wider text-slate-400 mb-1 font-semibold">
              My Role on the Project:
            </p>
            <p className="text-sm font-medium text-cyan-300">
              Full SEO Strategy · Redirect Architecture · Working with Developers · Content Updates · AI Search (HEO)
            </p>
          </div>
        </section>

        {/* SECTION 3: THE STEP-BY-STEP PROCESS */}
        <section id="framework" className="scroll-mt-24 mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-cyan-400" />
            <h2 className="text-xs font-mono uppercase tracking-widest text-cyan-400 font-bold">
              03 / The Process
            </h2>
          </div>

          <h3 className="font-heading font-bold text-2xl sm:text-3xl text-white tracking-tight mb-4">
            My 7-Step Migration Plan
          </h3>
          <p className="text-base text-slate-300 leading-relaxed mb-6">
            Instead of changing everything in one day, I used a structured 7-step process to keep the website safe and steady at every stage.
          </p>

          {/* Stepper Pipeline */}
          <div className="p-5 rounded-2xl bg-white/[0.02] border border-white/10 shadow-lg">
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
              {[
                { step: "01", name: "Audit", desc: "Checked all 8,000 pages" },
                { step: "02", name: "Prioritize", desc: "Picked top business pages" },
                { step: "03", name: "Migrate", desc: "Moved in small batches" },
                { step: "04", name: "Test", desc: "Tested before going live" },
                { step: "05", name: "Fix", desc: "Fixed broken links & tags" },
                { step: "06", name: "Optimize", desc: "Rewrote content for AI" },
                { step: "07", name: "Monitor", desc: "Tracked rankings daily" },
              ].map((item, idx) => (
                <div key={idx} className="relative p-3 rounded-xl bg-white/[0.03] border border-white/5 text-center group hover:border-cyan-500/40 transition-colors">
                  <span className="text-[10px] font-mono text-cyan-400 font-bold block mb-1">{item.step}</span>
                  <span className="text-xs font-bold text-white block mb-0.5">{item.name}</span>
                  <span className="text-[10px] text-slate-400 block leading-tight">{item.desc}</span>
                  {idx < 6 && (
                    <div className="hidden lg:block absolute -right-2 top-1/2 -translate-y-1/2 z-10 text-white/20">
                      →
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 4: WHAT I WORKED ON */}
        <section id="execution" className="scroll-mt-24 mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-cyan-400" />
            <h2 className="text-xs font-mono uppercase tracking-widest text-cyan-400 font-bold">
              04 / What I Did
            </h2>
          </div>

          <h3 className="font-heading font-bold text-2xl sm:text-3xl text-white tracking-tight mb-8">
            Five Key Projects That Saved the Migration
          </h3>

          <div className="space-y-8">

            {/* PROJECT 01 */}
            <article className="p-6 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="flex items-center justify-between gap-4 mb-3">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-400 font-mono text-xs font-bold flex items-center justify-center">
                    01
                  </span>
                  <h4 className="font-heading font-bold text-xl text-white">
                    Moving Pages in Phases (Not All at Once)
                  </h4>
                </div>
                <span className="text-[11px] font-mono uppercase text-slate-400 hidden sm:inline">Strategy</span>
              </div>

              <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-4">
                Moving 8,000 pages on a single day is very dangerous. If rankings drop, it is impossible to know which page caused the problem. I divided the migration into small groups and checked each page using clear data:
              </p>

              {/* Simple Decision Chips */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs font-mono mb-4">
                {[
                  "Google Search Traffic",
                  "Valuable Backlinks",
                  "Sales & Demo Value",
                  "Current Google Rank",
                  "Search Intent",
                  "Content Freshness",
                  "Google Index Status",
                  "Internal Link Strength"
                ].map((chip, idx) => (
                  <div key={idx} className="p-2 rounded-lg bg-white/[0.03] border border-white/5 flex items-center gap-1.5 text-slate-300">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 flex-shrink-0" />
                    <span className="truncate">{chip}</span>
                  </div>
                ))}
              </div>

              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                <strong className="text-white">The Outcome:</strong> We moved important pages first, combined thin pages, and removed old junk pages. This kept our Google ranking authority focused on our main business pages.
              </p>
            </article>

            {/* PROJECT 02 */}
            <article className="p-6 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="flex items-center justify-between gap-4 mb-3">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 font-mono text-xs font-bold flex items-center justify-center">
                    02
                  </span>
                  <h4 className="font-heading font-bold text-xl text-white">
                    Fixing Redirects at Scale
                  </h4>
                </div>
                <span className="text-[11px] font-mono uppercase text-slate-400 hidden sm:inline">Technical SEO</span>
              </div>

              <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-4">
                When an old URL changes, Google needs a clean permanent (301) redirect to the new page. The developers were using automated rules that were sending users to the wrong pages or causing temporary 302 errors.
              </p>

              {/* Simple Code Flow Box */}
              <div className="p-4 rounded-xl bg-[#050811] border border-white/10 font-mono text-xs space-y-2 mb-4">
                <div className="flex items-center justify-between text-slate-400 pb-2 border-b border-white/5 text-[11px]">
                  <span>Redirect Mapping Example</span>
                  <span className="text-cyan-400">1-to-1 Clean Redirect</span>
                </div>
                <div className="space-y-1 text-[12px] pt-1">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 text-slate-300">
                    <span className="text-red-300/80 line-through">wp.seismic.com/blog/sales-enablement-post</span>
                    <span className="text-amber-400 text-[11px]">302 (Wrong Redirect) ✗</span>
                  </div>
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 text-emerald-300 font-semibold">
                    <span>seismic.com/insights/sales-enablement/post</span>
                    <span className="text-emerald-400 text-[11px]">301 (Exact Match, Permanent) ✓</span>
                  </div>
                </div>
              </div>

              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                I tested thousands of URLs using crawler tools and mapped every single old URL to its exact new matching page. This stopped the redirect errors and passed 100% of our old ranking strength to the new site.
              </p>
            </article>

            {/* PROJECT 03 */}
            <article className="p-6 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="flex items-center justify-between gap-4 mb-3">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 font-mono text-xs font-bold flex items-center justify-center">
                    03
                  </span>
                  <h4 className="font-heading font-bold text-xl text-white">
                    Working Directly With the Dev Team
                  </h4>
                </div>
                <span className="text-[11px] font-mono uppercase text-slate-400 hidden sm:inline">Teamwork</span>
              </div>

              <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-4">
                Instead of just sending long reports after pages were already published, I worked side-by-side with the web developers every week. Before any new page layout was published, we tested:
              </p>

              {/* Dev Checks */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 text-xs font-mono mb-4">
                {[
                  { name: "Canonical Tags", desc: "Tells Google the main URL" },
                  { name: "Schema Data", desc: "Helps Google understand content" },
                  { name: "Robots Directives", desc: "Lets Google crawl easily" },
                  { name: "Fast Page Speed", desc: "Loads fast on phones and laptops" },
                ].map((item, idx) => (
                  <div key={idx} className="p-3 rounded-xl bg-white/[0.03] border border-white/5">
                    <span className="text-white font-semibold block mb-0.5">{item.name}</span>
                    <span className="text-[11px] text-slate-400 block">{item.desc}</span>
                  </div>
                ))}
              </div>

              <p className="text-xs sm:text-sm text-slate-400 leading-relaxed">
                Testing before launch helped us catch technical bugs early and fix them within 48 hours instead of letting them hurt traffic.
              </p>
            </article>

            {/* PROJECT 04 */}
            <article className="p-6 rounded-2xl bg-white/[0.02] border border-white/10">
              <div className="flex items-center justify-between gap-4 mb-3">
                <div className="flex items-center gap-3">
                  <span className="w-8 h-8 rounded-lg bg-purple-500/10 border border-purple-500/20 text-purple-400 font-mono text-xs font-bold flex items-center justify-center">
                    04
                  </span>
                  <h4 className="font-heading font-bold text-xl text-white">
                    Updating Internal Links Across the Site
                  </h4>
                </div>
                <span className="text-[11px] font-mono uppercase text-slate-400 hidden sm:inline">Site Structure</span>
              </div>

              <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-4">
                When page addresses change, internal links inside blog posts and navigation menus still point to old addresses. When users click them, they have to wait for redirects.
              </p>

              <div className="p-4 rounded-xl bg-white/[0.03] border border-white/10 text-xs sm:text-sm text-slate-300 space-y-2 mb-2">
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Updated Links to New URLs:</strong> Fixed old links so users and Google go straight to the new page without extra redirects.</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Better Link Descriptions:</strong> Used natural, clear anchor text that explains what each linked page is about.</span>
                </div>
                <div className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Linked to Key Business Pages:</strong> Added helpful links pointing to demo requests, product comparison pages, and ROI calculators.</span>
                </div>
              </div>
            </article>

          </div>
        </section>

        {/* SECTION 5: AI SEARCH STRATEGY */}
        <section id="ai-search" className="scroll-mt-24 mb-16">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-b from-cyan-950/40 via-[#0a1224] to-[#080c16] border border-cyan-500/30 p-6 sm:p-9 shadow-2xl">
            
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
              <h2 className="text-xs font-mono uppercase tracking-widest text-cyan-300 font-bold">
                05 / AI Search Strategy
              </h2>
            </div>

            <h3 className="font-heading font-extrabold text-2xl sm:text-3xl text-white tracking-tight mb-4">
              Building Content for Google AI Overviews &amp; Copilot (HEO)
            </h3>

            <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-6">
              Most website migrations only focus on regular Google search results. But today, buyers ask questions directly to AI engines like <strong className="text-white">Google AI Overviews and Microsoft Copilot</strong>. I used an **HEO (Human Experience Optimization)** plan to make sure Seismic ranks high in AI answers.
            </p>

            {/* Simple Comparison */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              
              <div className="p-4 rounded-2xl bg-white/[0.03] border border-white/10">
                <span className="text-[11px] font-mono uppercase text-slate-400 block mb-2 font-bold">
                  Old SEO Method
                </span>
                <ul className="space-y-2 text-xs text-slate-400 font-mono">
                  <li className="flex items-start gap-2">
                    <span className="text-red-400">✗</span>
                    <span>Repeating the same keyword over and over in the text</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-400">✗</span>
                    <span>Long, boring introductions before getting to the answer</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-red-400">✗</span>
                    <span>Writing only for bots instead of real human readers</span>
                  </li>
                </ul>
              </div>

              <div className="p-4 rounded-2xl bg-cyan-500/10 border border-cyan-500/30">
                <span className="text-[11px] font-mono uppercase text-cyan-300 block mb-2 font-bold">
                  New AI Search (HEO) Method
                </span>
                <ul className="space-y-2 text-xs text-cyan-100/90 font-mono">
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400">✓</span>
                    <span>Giving direct, clear answers to customer questions right at the top</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400">✓</span>
                    <span>Using clean bullet points, tables, and clear headings AI can quote</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-cyan-400">✓</span>
                    <span>Adding expert facts and schema so AI trusts our brand</span>
                  </li>
                </ul>
              </div>

            </div>

            {/* Result Callout */}
            <div className="p-4 sm:p-5 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-200 text-sm leading-relaxed flex items-start gap-3.5">
              <Sparkles className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" />
              <div>
                <strong className="text-white block font-semibold mb-1">The Result: +120% More AI Citations</strong>
                Within 6 months, Seismic became one of the top cited brands in Google AI Overviews and Bing Copilot for sales enablement and enterprise software searches.
              </div>
            </div>

          </div>
        </section>

        {/* SECTION 6: RESULTS & KEY OUTCOMES */}
        <section id="results" className="scroll-mt-24 mb-16">
          <div className="flex items-center gap-2 mb-3">
            <span className="w-2 h-2 rounded-full bg-cyan-400" />
            <h2 className="text-xs font-mono uppercase tracking-widest text-cyan-400 font-bold">
              06 / The Results
            </h2>
          </div>

          <h3 className="font-heading font-bold text-2xl sm:text-3xl text-white tracking-tight mb-4">
            How the Numbers Recovered and Grew
          </h3>
          <p className="text-base text-slate-300 leading-relaxed mb-8">
            By fixing redirect errors quickly and working closely with the developers, we stopped the traffic drop and reached new traffic records.
          </p>

          {/* Simple 3-Month Timeline */}
          <div className="space-y-4 mb-8">
            <div className="p-4 sm:p-5 rounded-2xl bg-white/[0.03] border border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3.5">
                <span className="px-2.5 py-1 rounded bg-blue-500/10 border border-blue-500/20 text-blue-300 font-mono text-xs font-bold mt-0.5">
                  Month 1 (Days 1–30)
                </span>
                <div>
                  <h4 className="text-white font-bold text-sm sm:text-base">Stopped the 20% Traffic Drop</h4>
                  <p className="text-xs sm:text-sm text-slate-400 mt-0.5">
                    Found and fixed broken 302 redirect loops, corrected wrong canonical tags, and mapped top pages to their right URLs.
                  </p>
                </div>
              </div>
              <span className="text-xs font-mono text-cyan-400 font-semibold flex-shrink-0">Traffic Drop Stopped</span>
            </div>

            <div className="p-4 sm:p-5 rounded-2xl bg-white/[0.03] border border-white/10 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3.5">
                <span className="px-2.5 py-1 rounded bg-indigo-500/10 border border-indigo-500/20 text-indigo-300 font-mono text-xs font-bold mt-0.5">
                  Month 2 (Days 31–60)
                </span>
                <div>
                  <h4 className="text-white font-bold text-sm sm:text-base">Google Indexed All New Pages</h4>
                  <p className="text-xs sm:text-sm text-slate-400 mt-0.5">
                    Google crawled the new Sitecore website smoothly with zero missing pages and clean structured data.
                  </p>
                </div>
              </div>
              <span className="text-xs font-mono text-indigo-400 font-semibold flex-shrink-0">100% Indexed</span>
            </div>

            <div className="p-4 sm:p-5 rounded-2xl bg-emerald-500/5 border border-emerald-500/30 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="flex items-start gap-3.5">
                <span className="px-2.5 py-1 rounded bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 font-mono text-xs font-bold mt-0.5">
                  Month 3 (Days 61–90+)
                </span>
                <div>
                  <h4 className="text-white font-bold text-sm sm:text-base">Full Traffic Recovery &amp; +120% AI Growth</h4>
                  <p className="text-xs sm:text-sm text-emerald-200/90 mt-0.5">
                    Organic traffic was completely back to normal, AI search visibility went up by +120%, and demo requests kept growing.
                  </p>
                </div>
              </div>
              <span className="text-xs font-mono text-emerald-400 font-semibold flex-shrink-0">Full Recovery ✓</span>
            </div>
          </div>

          {/* Simple Lessons Box */}
          <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10">
            <h4 className="text-xs font-mono uppercase tracking-wider text-slate-400 font-bold mb-3">
              Simple Lessons for Any Big Website Migration:
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs text-slate-300">
              <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                <span className="text-cyan-400 font-bold block mb-1">1. Don't move all pages in one day</span>
                <span>Move pages in small batches so you can test and fix errors easily.</span>
              </div>
              <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                <span className="text-cyan-400 font-bold block mb-1">2. Work with developers early</span>
                <span>Catch technical bugs together before publishing new pages live.</span>
              </div>
              <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                <span className="text-cyan-400 font-bold block mb-1">3. Update your internal links</span>
                <span>Change links inside your articles to point directly to the new URLs.</span>
              </div>
              <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                <span className="text-cyan-400 font-bold block mb-1">4. Write clear answers for AI</span>
                <span>Structure your content with direct answers so AI search engines cite you.</span>
              </div>
            </div>
          </div>
        </section>

        {/* EXPLORE OTHER CASE STUDIES */}
        <section className="mb-14 pt-8 border-t border-white/[0.08]">
          <h4 className="text-xs font-mono uppercase tracking-widest text-slate-400 mb-4 font-semibold">
            Explore Other Case Studies
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            
            <Link
              to="/casestudy/myoperator/"
              className="p-5 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-indigo-500/40 transition-all group flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between text-xs font-mono text-indigo-400 mb-2">
                  <span>MyOperator</span>
                  <span className="px-2 py-0.5 rounded bg-indigo-400/10 border border-indigo-400/20 text-[10px]">B2B SaaS</span>
                </div>
                <h5 className="font-heading font-bold text-white text-base group-hover:text-indigo-300 transition-colors mb-2">
                  200% Organic Surge &amp; 60% Pipeline Sourced
                </h5>
                <p className="text-xs text-slate-400 line-clamp-2">
                  Rebuilding SaaS content architecture, pruning low-value URLs, and turning organic search into the #1 revenue channel.
                </p>
              </div>
              <div className="mt-4 flex items-center gap-1.5 text-xs font-mono text-indigo-400 font-semibold">
                <span>Read Case Study</span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

            <Link
              to="/casestudy/workhours/"
              className="p-5 rounded-2xl bg-white/[0.02] border border-white/10 hover:border-cyan-500/40 transition-all group flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between text-xs font-mono text-cyan-400 mb-2">
                  <span>Workhours → Trydo School</span>
                  <span className="px-2 py-0.5 rounded bg-cyan-400/10 border border-cyan-400/20 text-[10px]">EdTech &amp; Growth</span>
                </div>
                <h5 className="font-heading font-bold text-white text-base group-hover:text-cyan-300 transition-colors mb-2">
                  From SEO Traffic to Student Growth (-60% Refunds)
                </h5>
                <p className="text-xs text-slate-400 line-clamp-2">
                  Scaling course-intent traffic by +35%, slashing refund rates by 60%, and automating email journeys with ConvertKit.
                </p>
              </div>
              <div className="mt-4 flex items-center gap-1.5 text-xs font-mono text-cyan-400 font-semibold">
                <span>Read Case Study</span>
                <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>

          </div>
        </section>

        {/* AUTHOR BIO & CTA */}
        <div className="p-6 sm:p-8 rounded-3xl bg-gradient-to-b from-[#0e1626] to-[#080c16] border border-white/10 shadow-2xl flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4 text-left">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-blue-600 via-indigo-600 to-cyan-400 text-white flex items-center justify-center font-bold text-lg flex-shrink-0 shadow-lg shadow-blue-500/20">
              KN
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-heading font-bold text-lg text-white">Kanna Narendra Kumar</h4>
                <span className="text-[10px] font-mono text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
                  Open for Work
                </span>
              </div>
              <p className="text-xs sm:text-sm text-slate-400 mt-1 max-w-md">
                Senior SEO Specialist &amp; AI Search Strategist with 8+ years helping SaaS companies grow organic traffic and handle complex migrations.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto justify-start md:justify-end">
            <Link
              to="/"
              className="px-4 py-2.5 rounded-xl bg-white/[0.06] hover:bg-white/[0.12] text-xs font-mono text-white transition-colors border border-white/5 flex items-center gap-2"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Back to Portfolio</span>
            </Link>
            <a
              href="mailto:narendrakumarkanna@gmail.com"
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-500 hover:to-cyan-400 text-xs font-mono text-white font-semibold transition-all shadow-md shadow-blue-500/20 flex items-center gap-2"
            >
              <span>Contact Me</span>
              <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>
        </div>

      </main>

      {/* Simple Footer */}
      <footer className="border-t border-white/[0.08] py-10 px-4 text-center text-xs font-mono text-slate-500">
        <div className="max-w-4xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <p>© {new Date().getFullYear()} Kanna Narendra Kumar · narendradigital.in</p>
          <div className="flex items-center gap-4 text-[11px] text-slate-400">
            <Link to="/" className="hover:text-white transition-colors">Portfolio Home</Link>
            <span>•</span>
            <a href="mailto:narendrakumarkanna@gmail.com" className="hover:text-white transition-colors">Contact</a>
            <span>•</span>
            <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="hover:text-white transition-colors">
              Back to Top ↑
            </button>
          </div>
        </div>
      </footer>

    </div>
  );
}
